import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Goal, User } from '../types';

export const useGoalsData = (companyId: number | null | undefined, currentUser?: User | null) => {
  const queryClient = useQueryClient();
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const fetchGoals = async () => {
    if (!companyId || !supabase) return [];
    const { data, error } = await supabase
      .from('goals')
      .select('*, assignee:users!goals_assigned_to_fkey(name)')
      .eq('company_id', companyId);
    if (error) throw error;
    return data?.map(goal => ({
      ...goal,
      assignee: goal.assignee
    })) || [];
  };

  const query = useQuery({
    queryKey: ['goals', companyId],
    queryFn: fetchGoals,
    enabled: !!companyId,
  });

  const addGoalMutation = useMutation({
    mutationFn: async (goal: Omit<Goal, 'id' | 'created_at' | 'company_id' | 'users' | 'assignee'>) => {
      if (!supabase) throw new Error('Supabase not initialized');
      const { data, error } = await supabase
        .from('goals')
        .insert([{ ...goal, company_id: companyId }])
        .select('*, assignee:users!goals_assigned_to_fkey(name)')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', companyId] });
    },
    onError: (e) => setErrorMsg('Erro ao adicionar meta: ' + e.message)
  });

  const deleteGoalMutation = useMutation({
    mutationFn: async (id: number) => {
      if (!supabase) throw new Error('Supabase not initialized');
      const { error } = await supabase.from('goals').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', companyId] });
    },
    onError: (e) => setErrorMsg('Erro ao deletar meta: ' + e.message)
  });

  const updateGoalMutation = useMutation({
    mutationFn: async ({ id, ...goal }: any) => {
      if (!supabase) throw new Error('Supabase not initialized');
      const { data, error } = await supabase
        .from('goals')
        .update(goal)
        .eq('id', id)
        .select('*, assignee:users!goals_assigned_to_fkey(name)')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals', companyId] });
    },
    onError: (e) => setErrorMsg('Erro ao atualizar meta: ' + e.message)
  });

  const clearError = () => setErrorMsg(null);

  return {
    goals: query.data || [],
    loading: query.isLoading,
    error: errorMsg || (query.isError ? 'Erro ao carregar metas' : null),
    clearError,
    refreshData: () => query.refetch(),
    addGoal: async (g: any) => await addGoalMutation.mutateAsync(g),
    updateGoal: async (g: any) => await updateGoalMutation.mutateAsync(g),
    deleteGoal: async (id: number) => await deleteGoalMutation.mutateAsync(id)
  };
};
